<template>
<div class="query-inclusion-builder query-inclusion-advanced">
    <div class="row my-3">
        <div class="col-md-9 col-12 mb-2">
            <div class="d-flex flex-column flex-md-row">
                <nav class="navbar navbar-expand inclusion-filter-bar secondary-tab-bar bg-secondary p-0 d-flex flex-nowrap align-items-stretch flex-grow-1"
                    :class="{ 'is-invalid': errors.logic_statement }">
                    <ul class="navbar-nav flex-row flex-nowrap">
                        <li class="nav-item">
                            <a href="#"
                                class="nav-link d-flex flex-nowrap secondary-tab text-nowrap"
                                :class="{ 'secondary-tab-active': data_filter.filter_option === 'all' }"
                                @click.prevent="updateFilterLogic('all')">
                                <span>All Filters</span>
                            </a>
                        </li>
                        <li class="nav-item">
                            <a href="#"
                                class="nav-link d-flex flex-nowrap secondary-tab text-nowrap"
                                :class="{ 'secondary-tab-active': data_filter.filter_option === 'any' }"
                                @click.prevent="updateFilterLogic('any')">
                                <span>Any Filters</span>
                            </a>
                        </li>
                        <li class="nav-item">
                            <a href="#"
                                class="nav-link d-flex flex-nowrap secondary-tab text-nowrap"
                                :class="{ 'secondary-tab-active': data_filter.filter_option === 'custom', 'no-right-rounded-corner': data_filter.filter_option === 'custom' }"
                                @click.prevent="updateFilterLogic('custom')">
                                <span>Custom Logic</span>
                            </a>
                        </li>
                    </ul>
                    <input type="text"
                        class="form-control border-0 flex-grow-1 ms-0"
                        v-model="data_filter.filter_logic"
                        v-show="data_filter.filter_option === 'custom'"
                        :class="{ 'is-invalid': errors.logic_statement }"
                        :disabled="!if_allow_edit"
                        placeholder="e.g. (1 and 2) or 3" aria-label="Filter Logic">
                </nav>
                <div class="invalid-feedback ms-2 text-danger d-block" v-if="errors.logic_statement">
                    {{data_filter.logic_error_msg}}
                </div>
            </div>
        </div>
        <div class="col-md-3 col-12 text-md-end text-center mb-0 mb-md-2">
            <button v-if="if_allow_edit" class="btn btn-secondary float-end" type="button" @click="addLogicUnit">
                <i class="fas fa-plus"></i> Add clause
            </button>
            <div v-else class="d-inline-block float-end" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                <button class="btn btn-secondary" :disabled="true">
                    <i class="fas fa-plus"></i> Add clause
                </button>
            </div>
        </div>
    </div>

    <div class="row logic-unit" v-for="(unit, index) in data_filter.logic_units" :key="index" :class="{ 'is-invalid' : hasLogicUnitError(unit) && errors.logic_units[index]}">
        <div class="col-md-1 py-1 text-center">
            <label class="form-label fs-7 m-0" >Unit #</label>
            <h3 class="m-0 mt-1">{{index+1}}</h3>
        </div>
        <div class="col-md-3 py-1 text-center">
            <label class="form-label fs-7 m-0 ">Column Name</label>
            <input 
                v-if="unit && unit.db_column && unit.db_column.if_sql_value" 
                :value="getColumnDisplayLabel(unit)"
                class="form-control" disabled data-bs-toggle="tooltip" title="Calculated fields cannot be edited" aria-label="Column Name">
            <v-select v-else 
                :key="'column-select-' + index"
                :options="db_columns" 
                :modelValue="unit.db_column"
                @update:modelValue="(val) => { unit.db_column = val; onColumnChange(unit); }"
                :searchable="true" 
                class="searchable-select column-display category"
                :selectable="(option) => option.text != ''"
                label="text" 
                @search="fetchOptions"
                :filterable="false"
                data-bs-toggle="tooltip" 
                :data-bs-original-title="getColumnTitle(unit)"
                @open="handleColumnDisplayOpen(index)"
                :disabled="!if_allow_edit">

                <template #selected-option>
                    {{ getColumnDisplayLabel(unit) }}
                </template>

                <template #open-indicator="{ attributes }">
                    <span v-bind="attributes" style="width: 12px; line-height: 8px;"><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg></span>
                </template>

                <template #option="{ text, category, subcategory }">
                    <div v-if="text == '' && !subcategory" :data-category="category" class="category-header"
                        @click="expandCategory(category, null)"
                        @mouseover="handleHover($event)"
                        data-isexpanded="false">
                        {{ category }} <i class="fas fa-caret-right"></i>
                    </div>
                    <div v-else-if="text == '' && subcategory"
                        :data-subcategory="category"
                        :data-subcategory-name="subcategory"
                        class="subcategory-header"
                        @click="expandSubcategory(category, subcategory, null)"
                        @mouseover="handleHover($event)"
                        data-isexpanded="false"
                        v-show="isSubcategoryVisible(category, subcategory)">
                        {{ subcategory }} <i class="fas fa-caret-right"></i>
                    </div>
                    <div v-else class="suboption" :data-subcategory="category" :data-subcategory-name="subcategory"
                        :class="shouldShowOption(category, subcategory)"
                        @mouseover="handleHover($event)">
                        {{getDisplayText(text)}}
                    </div>
                </template>
                <template #no-options="{ search, searching, loading }">
                    <div class="suboption show" v-if="is_loading">
                        <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> <span class="visually-hidden">Loading...</span></div>  Loading columns
                    </div>
                    <div class="suboption show" v-else>
                        <em>No results found</em>
                    </div>
                </template>
            </v-select>
        </div>
        <div class="py-1 text-center" :class="{ 'col-md-2' : unit.operator && unit.operator.value != 'IS NULL' && unit.operator.value != 'IS NOT NULL', 'col-md-8' : data_filter.logic_units.length <= 1 && (unit.operator.value == 'IS NULL' || unit.operator.value == 'IS NOT NULL'), 'col-md-7 col-9' : data_filter.logic_units.length > 1 && (unit.operator.value == 'IS NULL' || unit.operator.value == 'IS NOT NULL') }" >
            <label class="form-label fs-7 m-0">Operator</label>
            <v-select :key="'operator-select-' + index"
                :options="operator_options"
                :modelValue="unit.operator"
                @update:modelValue="unit.operator = $event; updateOperator(unit)"
                class="operator-select"
                :selectable="(option) => option.text != ''" label="text"
                :searchable="true" @search="fetchOperators"
                @open="handleOperatorOpen(index)"
                :disabled="!if_allow_edit">

                <template #open-indicator="{ attributes }">
                    <span v-bind="attributes" style="width: 12px; line-height: 8px;"><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg></span>
                </template>

                <template #option="{ text, category }">
                    <div v-if="text == ''" :data-category="category" class="category-header operator-category"
                        @click="expandOperatorCategory(category)"
                        :data-isexpanded="false"
                        @mouseover="handleOperatorHover($event)">
                        {{ category }} <i class="fas fa-caret-right"></i>
                    </div>
                    <div v-else class="suboption operator-option"
                        :class="{ show: true }"
                        :data-category="category"
                        @mouseover="handleOperatorHover($event)">
                        {{ text }}
                    </div>
                </template>
            </v-select>
        </div>
        <div class="py-1 text-center" :class="{ 'col-md-5 col-11' : data_filter.logic_units.length > 1, 'col-md-5' : data_filter.logic_units.length <= 1}"
                v-if="shouldShowValues(unit)">
            <label class="form-label fs-7 m-0">Value</label>
            <!-- VALUE INPUT AREA -->
            <!-- FILE/DATASET SELECTION -->
            <div v-if="shouldShowFileSelection(unit)" :class="(unit.selected_file != null && !isSegmentOperator(unit.operator)) ? 'input-group' : 'w-100'">
                <v-select v-model="unit.selected_file"
                    :options="getAvailableOptions(unit)"
                    :searchable="true"
                    data-bs-toggle="tooltip" 
                    :data-bs-original-title="unit.selected_file?.text"
                    label="text"
                    :placeholder="getPlaceholderText(unit)"
                    @search="searchFiles"
                    @update:modelValue="handleFileSelection(index)"
                    class="searchable-select column-display category"
                    :class="(unit.selected_file && !isSegmentOperator(unit.operator)) ? 'w-50 me-0' : 'w-100'"
                    :key="'file-select-' + index"
                    :disabled="!if_allow_edit">
                    <template #option="{ text, created_at, type, source }">
                        <div class="suboption show text-wrap">
                            {{ text }} 
                            <small class="text-muted" v-if="type === 'file'">
                                (Uploaded: {{ formatDate(created_at) }})
                            </small>
                        </div>
                    </template>
                    <template #no-options="{ search, searching, loading }">
                        <div class="suboption show" v-if="is_loading">
                            <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> <span class="visually-hidden">Loading...</span></div>  {{ getLoadingText(unit) }}
                        </div>
                        <div class="suboption show" v-else>
                            <em>No Results Found</em>
                        </div>
                    </template>
                    <template #list-footer="{ search }">
                        <li v-if="hasMoreFiles && (unit.operator.value === 'IN_FILE' || unit.operator.value === 'NOT_IN_FILE')" class="border-top border-primary border-1 text-center">
                            <a class="btn btn-sm btn-link pb-0 text-nowrap" @click.prevent="loadMoreFiles">
                                Load More
                            </a>
                        </li>
                    </template>
                </v-select>
                <v-select v-if="unit.selected_file && !isSegmentOperator(unit.operator)"
                    v-model="unit.selected_column"
                    :options="unit.selected_file.columns"
                    :searchable="true"
                    label="label"
                    data-bs-toggle="tooltip"
                    :data-bs-original-title="unit.selected_column?.label"
                    :title="unit.selected_column?.label"
                    placeholder="Select a column..."
                    @update:modelValue="handleColumnSelection(index)"
                    class="searchable-select column-display category w-50"
                    :key="'column-select-file-' + index"
                    :disabled="!if_allow_edit">
                    <template #option="{ id, label }">
                        <div class="suboption show">{{ label }}</div>
                    </template>
                    <template #no-options="{ search, searching, loading }">
                        <div class="suboption show" v-if="is_loading">
                            <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> <span class="visually-hidden">Loading...</span></div>  Loading columns
                        </div>
                        <div class="suboption show" v-else>
                            <em>No results found</em>
                        </div>
                    </template>
                </v-select>
            </div>

            <!-- DATE SELECTION (match QueryBuilderSimplified: datepicker or date comparison number+select) -->
            <div v-else-if="shouldShowDate(unit)" class="d-flex align-items-center input-group">
                <button class="btn btn-secondary dropdown-toggle selection-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false"
                    :disabled="!if_allow_edit">
                    <span v-html='typeOfSelection(unit)'></span>
                </button>
                <ul class="dropdown-menu dropdown-menu-primary" aria-labelledby="dropdownMenuButton1">
                    <li>
                        <a class="dropdown-item" href="#"
                            @click.prevent="unit.is_static = false; unit.show_options = false; unit.value = '';">Compare to Column</a></li>
                    <li>
                        <a class="dropdown-item" href="#"
                            v-if="unitIsDateType(unit)"
                            @click.prevent="unit.is_static = false; unit.show_options = true; unit.value = '';">Date Comparisons</a>
                    </li>
                    <li><a class="dropdown-item" href="#"
                        @click.prevent="unit.is_static = true; unit.show_options = false; unit.value = '';">Static Value</a></li>
                </ul>
                <!-- STATIC DATE PICKER -->
                <datepicker-utc 
                    v-if="unit.is_static && !unit.show_options && unitIsDateType(unit)"
                    :modelValue="unit.value"
                    @update:modelValue="unit.value = $event"
                    inputFormat="M/d/yyyy"
                    inputClass="form-control date"
                    style="width: 100%;"
                    :disabled="!if_allow_edit" />

                <!-- DATABASE COLUMN SELECTION -->
                <v-select :key="'value-db-select-' + index"
                    :options="db_columns" 
                    :modelValue="unit.value"
                    @update:modelValue="unit.value = $event; changeDBColumn(unit)"
                    :searchable="true" 
                    class="searchable-select value_db_select vue_select column-display category"
                    :selectable="(option) => option.text != ''"
                    label="text" 
                    @search="fetchOptions"
                    v-if="unit.is_static == false && !unit.show_options"
                    :filterable="false"
                    :disabled="!if_allow_edit"
                    @open="handleColumnDisplayOpen(index)"
                    :data-bs-toggle="'tooltip'"
                    :data-bs-original-title="getColumnTitle({ db_column: unit.value })"
                >
                    <template #selected-option="{ text, category, subcategory }">
                        {{(category != null && category != '' && category != 'list-header' ? category + ': ' : '')}}{{subcategory != null && subcategory != '' ? subcategory + ' › ' : ''}}{{text}}
                    </template>
                    <template #open-indicator="{ attributes }">
                        <span v-bind="attributes" style="width: 12px; line-height: 8px;"><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg></span>
                    </template>
                    <template #option="{ text, category, subcategory }">
                        <div v-if="text == '' && !subcategory" :data-category="category" class="category-header"
                            @click="expandCategory(category, null)"
                            @mouseover="handleHover($event)"
                            data-isexpanded="false">
                            {{ category }} <i class="fas fa-caret-right"></i>
                        </div>
                        <div v-else-if="text == '' && subcategory"
                            :data-subcategory="category"
                            :data-subcategory-name="subcategory"
                            class="subcategory-header"
                            @click="expandSubcategory(category, subcategory, null)"
                            @mouseover="handleHover($event)"
                            data-isexpanded="false"
                            v-show="isSubcategoryVisible(category, subcategory)">
                            {{ subcategory }} <i class="fas fa-caret-right"></i>
                        </div>
                        <div v-else class="suboption" :data-subcategory="category" :data-subcategory-name="subcategory"
                            :class="shouldShowOption(category, subcategory)"
                            @mouseover="handleHover($event)">
                            {{ text }}
                        </div>
                    </template>
                    <template #no-options="{ search, searching, loading }">
                        <div class="suboption show" v-if="is_loading">
                            <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> 
                                <span class="visually-hidden">Loading...</span>
                            </div>  
                            Loading columns
                        </div>
                        <div class="suboption show" v-else>
                            <em>No results found</em>
                        </div>
                    </template>
                </v-select>
                <!-- DATE COMPARISONS (number + unit, like QueryBuilderSimplified) -->
                <template v-if="!unit.is_static && unit.show_options && unitIsDateType(unit)">
                    <input type="number" class="form-control date_num ps-2"
                        :value="dateNumCalc(unit.value)"
                        :data-index="index" placeholder="###"
                        @change="updateDateValue(index, unit);"
                        :disabled="!if_allow_edit" aria-label="###">

                    <select class="form-select input-group-prepend date_comparison"
                        :value="dateComparisonCalc(unit.value)"
                        :data-index="index"
                        @change="updateDateValue(index, unit);"
                        :disabled="!if_allow_edit" aria-label="Select option">
                        <option value="day">Days Ago</option>
                        <option value="week">Weeks Ago</option>
                        <option value="month">Months Ago</option>
                        <option value="year">Years Ago</option>
                    </select>
                </template>
            </div>

            <!-- STATIC/LIST/COLUMN VALUE INPUT -->
            <div v-else class="input-group d-flex">
                <div class="input-group-prepend d-flex selection-toggle-wrapper" style="flex: 0 0 auto; min-width: 0;">
                    <button class="btn btn-secondary dropdown-toggle selection-toggle" 
                    type="button" data-bs-toggle="dropdown" aria-expanded="false" :disabled="unit.db_column=='' || !if_allow_edit"
                    style="border-top-right-radius: 0px; border-bottom-right-radius: 0px; white-space: nowrap;">
                        {{ typeOfSelection(unit) }}
                    </button>
                    <ul class="dropdown-menu dropdown-menu-primary" aria-labelledby="dropdownMenuButton1">
                        <li><a class="dropdown-item" href="#"
                            @click.prevent="unit.is_static = false; unit.show_options = false; unit.value = '';">Compare to Column</a></li>
                        <li>
                            <a class="dropdown-item" href="#"
                                v-if="unit.data_type!='date'"
                                @click.prevent="unit.is_static = true; unit.show_options = true; unit.value = ''; ensureOptionsLoaded(unit);"
                                >List of Options</a>
                        </li>
                        <li>
                            <a class="dropdown-item" href="#"
                                v-if="unit.data_type=='date'"
                                @click.prevent="unit.is_static = false; unit.show_options = true; unit.value = '';"
                                >Date Comparisons</a>
                        </li>
                        <li><a class="dropdown-item" href="#"
                        @click.prevent="unit.is_static = true; unit.show_options = false; unit.value = '';"
                        >Static Value</a></li>
                    </ul>
                </div>
                <!-- STATIC TEXT BOX -->
                <input type="text" class="form-control ps-2" :modelValue="unit.value"
                    :value="unit.value"
                    @input="unit.value = $event.target.value"
                    v-if="unit.is_static && unit.show_options == false && !isDate(unit.data_type)"
                    @change="updateStaticValue(unit)"
                    :disabled="!if_allow_edit" aria-label="Text input">

                <!-- STATIC WITH DATE FORMATTING -->
                <datepicker-utc 
                    :modelValue="unit.value"
                    @update:modelValue="unit.value = $event"
                    inputFormat="M/d/yyyy"
                    inputClass="form-control date"
                    style="width: 100%;"
                    v-if="unit.is_static && unit.show_options == false && isDate(unit.data_type)"
                    :disabled="!if_allow_edit" />

                <!-- DATABASE COLUMN SELECTION -->
                <v-select :key="'value-db-select-' + index"
                    :options="db_columns" 
                    :modelValue="unit.value"
                    @update:modelValue="unit.value = $event; changeDBColumn(unit)"
                    :searchable="true" 
                    class="searchable-select value_db_select vue_select column-display category"
                    :selectable="(option) => option.text != ''"
                    label="text" 
                    @search="fetchOptions"
                    v-if="unit.is_static == false && !unit.show_options"
                    :filterable="false"
                    :disabled="!if_allow_edit"
                    @open="handleColumnDisplayOpen(index)"
                    :data-bs-toggle="'tooltip'"
                    :data-bs-original-title="getColumnTitle({ db_column: unit.value })"
                >
                    <template #selected-option="{ text, category, subcategory }">
                        {{(category != null && category != '' && category != 'list-header' ? category + ': ' : '')}}{{subcategory != null && subcategory != '' ? subcategory + ' › ' : ''}}{{text}}
                    </template>
                    <template #open-indicator="{ attributes }">
                        <span v-bind="attributes" style="width: 12px; line-height: 8px;"><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg></span>
                    </template>
                    <template #option="{ text, category, subcategory }">
                        <div v-if="text == '' && !subcategory" :data-category="category" class="category-header"
                            @click="expandCategory(category, null)"
                            @mouseover="handleHover($event)"
                            data-isexpanded="false">
                            {{ category }} <i class="fas fa-caret-right"></i>
                        </div>
                        <div v-else-if="text == '' && subcategory"
                            :data-subcategory="category"
                            :data-subcategory-name="subcategory"
                            class="subcategory-header"
                            @click="expandSubcategory(category, subcategory, null)"
                            @mouseover="handleHover($event)"
                            data-isexpanded="false"
                            v-show="isSubcategoryVisible(category, subcategory)">
                            {{ subcategory }} <i class="fas fa-caret-right"></i>
                        </div>
                        <div v-else class="suboption" :data-subcategory="category" :data-subcategory-name="subcategory"
                            :class="shouldShowOption(category, subcategory)"
                            @mouseover="handleHover($event)">
                            {{ text }}
                        </div>
                    </template>
                    <template #no-options="{ search, searching, loading }">
                        <div class="suboption show" v-if="is_loading">
                            <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> 
                                <span class="visually-hidden">Loading...</span>
                            </div>  
                            Loading columns
                        </div>
                        <div class="suboption show" v-else>
                            <em>No results found</em>
                        </div>
                    </template>
                </v-select>
                <!-- SELECT FROM AVAILABLE DB VALUES -->
                <v-select :key="'value-select-' + index"
                    :id="index" 
                    v-if="unit.is_static && unit.show_options" 
                    :options="db_column_values[unit.db_column.value]" 
                    :modelValue="unit.value"
                    @update:modelValue="unit.value = $event"
                    :searchable="true" ref="valueSelect" class="searchable-select vue-multi-select multi-select-fixed"
                    :selectable="(option) => option.text != ''" @open="setEditingIndex(index)"
                    label="text" @search="debouncedSearchOptions"
                    :filterable="false" multiple
                    :disabled="!if_allow_edit">
                    <template #open-indicator="{ attributes }">
                        <span v-bind="attributes" style="width: 12px; line-height: 8px;"><svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'><path fill='none' stroke='#343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/></svg></span>
                    </template>
                    <template #option="{ text, category }">
                        <div v-if="text == ''" :data-category="category" class="category-header"
                        @click="expandCategory(category, null)"
                        data-isexpanded="false">
                        {{ category }} <i class="fas fa-caret-right"></i>
                        </div>
                        <div v-else class="suboption" :data-subcategory="category"
                        :class="category==null || category==''? 'show' : ''">
                        {{ text }}
                        </div>
                    </template>
                    <template #no-options="{ search, searching, loading }">
                        <div class="suboption show" v-if="is_loading">
                            <div class="d-inline-block spinner-border spinner-border-sm ms-2" role="status"> <span class="visually-hidden">Loading...</span></div>  Loading columns
                        </div>
                        <div class="suboption show" v-else>
                            <em>No results found</em>
                        </div>
                    </template>
                </v-select>
                <!-- DATE COMPARISONS (when in general block with date type - e.g. after switch from date block) -->
                <template v-if="!unit.is_static && unit.show_options && unitIsDateType(unit)">
                    <input type="number" class="form-control date_num ps-2"
                        :value="dateNumCalc(unit.value)"
                        :data-index="index" placeholder="###"
                        @change="updateDateValue(index, unit);"
                        :disabled="!if_allow_edit" aria-label="###">

                    <select class="form-select input-group-prepend date_comparison"
                        :value="dateComparisonCalc(unit.value)"
                        :data-index="index"
                        @change="updateDateValue(index, unit);"
                        :disabled="!if_allow_edit" aria-label="Select option">
                        <option value="day">Days Ago</option>
                        <option value="week">Weeks Ago</option>
                        <option value="month">Months Ago</option>
                        <option value="year">Years Ago</option>
                    </select>
                </template>
            </div>
            <div class="form-check text-start" v-if="unit.operator != null && (unit.operator.value == 'LIKE' || unit.operator.value == 'NOT LIKE' || unit.operator.value == 'IN' || unit.operator.value == 'NOT IN' || ((unit.operator == '=' || unit.operator.value == '=' || unit.operator.value == '<>' || unit.operator == '<>') && (unit.data_type=='string')))">
                <label class="form-check-label">
                    <input class="form-check-input" type="checkbox" value="LOWER" :modelValue="unit.modification"
                        @update:modelValue="unit.modification = $event"
                        :disabled="!if_allow_edit">
                    <small style="margin-top: 2px; display: inline-block;">Case-insensitive comparison</small>
                </label>
            </div>
            <small v-if="unit.operator != null && (unit.operator.value=='IN' || unit.operator.value=='NOT IN')">Comma separate the unique values. Quotes around strings are <em>not</em> needed.</small>
        </div>
        <div class="col-1 py-1" v-if="data_filter.logic_units.length > 1" :class="data_filter.logic_units.length > 1 && index == 0 ? 'mt-4 mt-md-3' : 'mt-4 mt-md-0'">
            <button v-if="if_allow_edit" class="btn btn-warning btn-icon-only-md" @click="removeLogicUnit(index);">
                <img class="icon" src="/img/icons/avid-icons/trash.svg" alt=""> 
            </button>
            <div v-else class="d-inline-block" data-bs-toggle="tooltip" title="You do not have privileges to complete this action">
                <button class="btn btn-secondary btn-icon-only-md" :disabled="true">
                    <img class="icon" src="/img/icons/avid-icons/trash.svg" alt=""> 
                </button>
            </div>
        </div>
        <div class="pb-3 m-0 col-12 invalid-feedback text-center" v-if="hasLogicUnitError(unit) && errors.logic_units[index]">
            All of the fields above must be completed.
        </div>
    </div>
</div>

</template>

<script>
    import debounce from 'lodash.debounce';
    import moment from 'moment';
    import vSelect from "vue3-select";

    export default {
        name: 'QueryInclusionAdvanced',
        props: {
            modelValue: {
                type: Object,
                required: true
            },
            client: {
                type: Object,
                required: true
            },
            source: {
                type: String,
                default: 'client_db'
            },
            data_source_id: {
                type: [String, Number],
                default: null
            },
            static_db_columns: {
                type: Object,
                default: null
            },
            include_trans: {
                type: Boolean,
                default: false
            },
            if_allow_edit: {
                type: Boolean,
                default: true
            }
        },
        components: {
            vSelect
        },
        emits: ['update:modelValue'],
        data() {
            return {
                db_columns: [],
                unfiltered_db_columns: [],
                db_column_values: [],
                unfiltered_db_column_values: [],
                db_search: [],
                debouncedSearchOptions: null,
                editing_index: null,
                errors: {
                    logic_units: [ false ],
                    logic_statement: false
                },
                data_filter: this.modelValue ? JSON.parse(JSON.stringify(this.modelValue)) : this.getBlankDataFilter(),
                is_loading: false,
                is_searching: false,
                expanded_categories: [],
                expanded_subcategories: [],
                expanded_operator_categories: [],
                is_updating: false,
                cached_custom_logic: '',
                unfiltered_operator_options: [
                    { category: "Common Operators", value: '', text: '' },
                    { category: "Common Operators", value: '=', text: 'Equals' },
                    { category: "Common Operators", value: '<>', text: 'Does Not Equal' },
                    { category: "Common Operators", value: 'IS NULL', text: 'Is Blank' },
                    { category: "Common Operators", value: 'IS NOT NULL', text: 'Is Not Blank' },
                    { category: "Common Operators", value: 'IN', text: 'Is One Of' },
                    { category: "Common Operators", value: 'NOT IN', text: 'Is Not One Of' },

                    { category: "Text Operators", value: '', text: '' },
                    { category: "Text Operators", value: 'LIKE', text: 'Contains' },
                    { category: "Text Operators", value: 'NOT LIKE', text: 'Does Not Contain' },
                    { category: 'Text Operators', value:"REGEXP_CONTAINS", text: 'Contains (Regex)' },
                    { category: 'Text Operators', value:"NOT REGEXP_CONTAINS", text: 'Does Not Contain (Regex)' },

                    { category: "Number Operators", value: '', text: '' },
                    { category: "Number Operators", value: '>', text: '>' },
                    { category: "Number Operators", value: '>=', text: '>=' },
                    { category: "Number Operators", value: '<', text: '<' },
                    { category: "Number Operators", value: '<=', text: '<=' },

                    { category: "Date Operators", value: '', text: '' },
                    { category: "Date Operators", value: '>', text: 'Is After' },
                    { category: "Date Operators", value: '>=', text: 'Is On or After' },
                    { category: "Date Operators", value: '<', text: 'Is Before' },
                    { category: "Date Operators", value: '<=', text: 'Is On or Before' },

                    { category: "Comparison Operators", value: '', text: '' },
                    { category: "Comparison Operators", value: 'IN_FILE', text: 'Is In File' },
                    { category: "Comparison Operators", value: 'NOT_IN_FILE', text: 'Is Not In File' },
                    { category: "Comparison Operators", value: 'IN_DATASET', text: 'Is In Dataset' },
                    { category: "Comparison Operators", value: 'NOT_IN_DATASET', text: 'Is Not In Dataset' },
                    //{ category: "Comparison Operators", value: 'IN_AUDIENCE', text: 'Is In Audience' },
                    //{ category: "Comparison Operators", value: 'NOT_IN_AUDIENCE', text: 'Is Not In Audience' },
                ],
                operator_options: [],
                // --- ADDED FOR FILE/DATASET SUPPORT ---
                available_files: [],
                available_datasets: [],
                file_search_offset: 0,
                file_search_pattern: '',
                hasMoreFiles: false,
                total_files: 0,
                total_datasets: 0,
                files_loaded: false,
                // --------------------------------------
            };
        },
        created() {
            this.debouncedSearchOptions = debounce(this.searchOptions, 500);
            // Initialize operator_options with unfiltered_operator_options
            this.operator_options = JSON.parse(JSON.stringify(this.unfiltered_operator_options));
        },
        beforeMount() {
            if(this.modelValue != null) {
                this.data_filter = JSON.parse(JSON.stringify(this.modelValue));
                this.normalizeLoadedLogicUnits(this.data_filter);
            }
            else
                this.data_filter = this.getBlankDataFilter();

            this.data_filter.filter_ui = "advanced";
            if (this.data_filter.filter_option === 'custom') {
                this.cached_custom_logic = this.data_filter.filter_logic;
            }
        },
        mounted() {
            this.loadDropdownColumns();
            this.initTooltips();
        },
        updated() {
            this.initTooltips();
        },
        watch: {
            data_filter: {
                handler(newVal) {
                    if (!this.is_updating) {
                        this.is_updating = true;
                        this.$emit('update:modelValue', JSON.parse(JSON.stringify(newVal)));
                        this.$nextTick(() => {
                            this.is_updating = false;
                        });
                    }
                },
                deep: true
            },
            modelValue: {
                handler(newVal) {
                    if (newVal && !this.is_updating) {
                        this.is_updating = true;
                        this.data_filter = JSON.parse(JSON.stringify(newVal));
                        this.normalizeLoadedLogicUnits(this.data_filter);
                        if (this.data_filter.filter_option === 'custom') {
                            this.cached_custom_logic = this.data_filter.filter_logic;
                        }
                        this.$nextTick(() => {
                            this.is_updating = false;
                        });
                    }
                },
                immediate: true
            },
            'data_filter.filter_logic': {
                handler(newVal) {
                    if (this.data_filter && this.data_filter.filter_option === 'custom') {
                        this.cached_custom_logic = newVal;
                    }
                }
            },
            source(old_pv, new_pv) {
                this.loadDropdownColumns();
            },
            data_source_id(old_pv, new_pv) {
                this.loadDropdownColumns();
            }
        },
        methods: {
            isFormValid() {
                //Reset the error messages
                for(var i = 0; i < this.data_filter.logic_units.length; i++)
                    this.errors.logic_units[i] = false;

                var form_is_valid = true;
                for(var i = 0; i < this.data_filter.logic_units.length; i++) {
                    this.errors.logic_units[i] = this.hasLogicUnitError(this.data_filter.logic_units[i]);
                    if(this.errors.logic_units[i])
                        form_is_valid = false;
                }
                this.errors.logic_statement = this.showCustomError();

                if(!form_is_valid || this.errors.logic_statement)
                    this.$forceUpdate();

                return form_is_valid && !this.errors.logic_statement;
            },
            getBlankDataFilter() {
                return {
                    filter_option: "all",
                    filter_logic: '1',
                    logic_error_msg: "",
                    filter_ui: 'advanced',
                    logic_units: [
                        {
                            db_column:{ value: "", text: "", category: ""},
                            data_type:'string',
                            operator: { value: "" , text: "", category: ""},
                            is_static: true,
                            show_options: false,
                            value:'',
                            modification:''
                        }
                    ]
                }
            },
            /**
             * Normalize logic_units when loading from API/simple view: ensure operator is an object,
             * and is_static/show_options are set so the correct value UI (date, options, static) renders.
             */
            normalizeLoadedLogicUnits(dataFilter) {
                if (!dataFilter?.logic_units?.length) return;
                const opts = this.unfiltered_operator_options || [];
                dataFilter.logic_units.forEach(unit => {
                    if (!unit) return;
                    // Normalize operator: string -> { value, text, category }
                    if (typeof unit.operator === 'string') {
                        const val = unit.operator;
                        const found = opts.find(o => o.value === val);
                        unit.operator = found ? { ...found } : { value: val, text: val, category: '' };
                    } else if (unit.operator == null) {
                        unit.operator = { value: '', text: '', category: '' };
                    }
                    // Normalize value / is_static / show_options so the right input shows
                    const isDateType = unit.data_type && (String(unit.data_type).indexOf('date') > -1 || String(unit.data_type).indexOf('time') > -1);
                    const val = unit.value;
                    if (isDateType && typeof val === 'string' && (val.includes('DATE_SUB') || val.includes('DATE_ADD'))) {
                        unit.show_options = true;
                        unit.is_static = false;
                    } else if (isDateType) {
                        unit.is_static = true;
                        unit.show_options = false;
                    } else if (val != null && Array.isArray(val) && val.length > 0) {
                        unit.show_options = true;
                        unit.is_static = true;
                    } else {
                        if (unit.is_static === undefined) unit.is_static = true;
                        if (unit.show_options === undefined) unit.show_options = false;
                    }
                });
            },
            dateNumCalc(value) {
                if(value == undefined || moment.isDate(value) || value.indexOf("DATE_SUB") == -1)
                    return value;
                value = value.replace("DATE_SUB(CURRENT_DATE(), INTERVAL ", "");

                var parts = value.split(" ");
                return parts[0];
            },
            dateComparisonCalc(value) {
                if(value == undefined || moment.isDate(value) || value.indexOf("DATE_SUB") == -1)
                    return value;
                value = value.replace("DATE_SUB(CURRENT_DATE(), INTERVAL ", "");

                var parts = value.split(" ");
                parts[1] = parts[1].replace(")", "");
                return parts[1];
            },
            isComparison(data_type) {
                if(data_type == undefined)
                    return true;

                return data_type.indexOf('float') > -1 ||
                    data_type.indexOf('int') > -1 || data_type.indexOf('date') > -1 || data_type.indexOf('time') > -1;
            },
            isDate(data_type) {
                if(data_type == undefined)
                    return false;

                return data_type.indexOf('date') > -1 || data_type.indexOf('time') > -1;
            },
            unitIsDateType(unit) {
                if (!unit) return false;
                if (this.isDate(unit.data_type)) return true;
                return unit.db_column && unit.db_column.text && this.guessDataType(unit.db_column.text) === 'date';
            },
            typeOfSelection(unit) {
                if(unit.is_static && unit.show_options )
                    return 'Options';
                else if(unit.is_static)
                    return "Static";
                else if(unit.show_options)
                    return 'Date';
                else return "Column";
            },
            loadDropdownColumns() {
                if(this.static_db_columns == null)
                    this.loadDatabaseColumns();
                else {
                    //loop through the static_db_columns object
                    for(var key in this.static_db_columns) {
                        this.db_columns.push({
                            value: key,
                            text: this.static_db_columns[key],
                            category: "",
                            if_sql_value: this.isSqlValue(key)
                        });
                    }
                    this.unfiltered_db_columns = JSON.parse(JSON.stringify(this.db_columns));
                }
            },
            loadDatabaseColumns() {
                this.is_loading = true;
                var _self = this;
                var data = {
                    client: this.client,
                    include_trans: this.include_trans,
                    source: this.source,
                    data_source_id: this.data_source_id
                };

                window.axios.post('/api/bigquery/get_db_columns', data)
                    .then(response => {
                    if(response.status != 200){
                        _self.db_columns = undefined;
                        _self.unfiltered_db_columns = undefined;
                    }else{
                        // Get current unit column values
                        const currentUnitColumns = new Set();
                        if (this.data_filter && this.data_filter.logic_units) {
                            this.data_filter.logic_units.forEach(unit => {
                                if (unit.db_column && unit.db_column.value) {
                                    currentUnitColumns.add(unit.db_column.value);
                                }
                            });
                        }

                        // Filter columns based on hidden field pattern
                        var cols = response.data.columns.filter((col) => {
                            const isHiddenField = /(?:^|\.)_/.test(col.value);
                            if (isHiddenField) {
                                // If it's a hidden field, only keep it if it's in current units
                                return currentUnitColumns.has(col.value);
                            }
                            return true;
                        });
                        var formatted_cols = [];
                        var current_category = null;
                        var current_subcategory = null;

                        // Store existing db_column values that don't match API response
                        const existingColumns = new Map();
                        if (this.data_filter && this.data_filter.logic_units) {
                            this.data_filter.logic_units.forEach(unit => {
                                if (unit.db_column && unit.db_column.value) {
                                    existingColumns.set(unit.db_column.value, unit.db_column);
                                }
                            });
                        }

                        // Sort columns to ensure proper nesting
                        cols.sort((a, b) => {
                            // First sort by category
                            if(a.value.indexOf(".") == -1 && b.value.indexOf(".") != -1)
                                return -1;
                            else if(a.value.indexOf(".") != -1 && b.value.indexOf(".") == -1)
                                return 1;
                            
                            // If same category, sort by subcategory presence
                            if(a.value.indexOf(".") != -1 && b.value.indexOf(".") != -1) {
                                const aParts = a.value.split(".");
                                const bParts = b.value.split(".");
                                
                                // If same category
                                if(aParts[0] === bParts[0]) {
                                    // If one has subcategory and other doesn't, put the one without subcategory first
                                    if(aParts.length === 2 && bParts.length > 2) return -1;
                                    if(aParts.length > 2 && bParts.length === 2) return 1;
                                    
                                    // If both have 2 parts (category.field), sort by field name alphabetically
                                    if(aParts.length === 2 && bParts.length === 2) {
                                        return aParts[1].localeCompare(bParts[1]);
                                    }
                                    // If both have subcategories (3+ parts), sort by subcategory
                                    if(aParts.length > 2 && bParts.length > 2) {
                                        return aParts[1].localeCompare(bParts[1]);
                                    }
                                }
                                return aParts[0].localeCompare(bParts[0]);
                            }
                            
                            return a.value.localeCompare(b.value);
                        });

                        // Create a Set of API response values for quick lookup
                        const apiValues = new Set(cols.map(col => col.value));

                        for(let i = 0; i < cols.length; i++) {
                            let col = cols[i];
                            let category = null;
                            let subcategory = null;
                            let text = col.text;

                            if(col.value.indexOf(".") != -1) {
                                let parts = col.value.split(".");
                                category = this.propercase(parts[0]);
                                
                                if(parts.length > 2) {
                                    subcategory = this.propercase(parts[1]);
                                    // For 3+ levels, join remaining parts with ›
                                    text = this.propercase(parts.slice(2).join(' › '));
                                } else {
                                    text = this.propercase(parts[1]);
                                }

                                // Add category divider if category changes
                                if(category != current_category) {
                                    formatted_cols.push({
                                        value: "DIVIDER-"+ category,
                                        category: category,
                                        text: "",
                                        subcategory: null
                                    });
                                    current_subcategory = null; // Reset subcategory when category changes
                                }

                                // Add subcategory divider if subcategory changes
                                if(subcategory && subcategory != current_subcategory) {
                                    formatted_cols.push({
                                        value: "DIVIDER-"+ category + "-" + subcategory,
                                        category: category,
                                        subcategory: subcategory,
                                        text: ""
                                    });
                                }
                            }

                            current_category = category;
                            current_subcategory = subcategory;

                            const isHiddenField = /(?:^|\.)_/.test(col.value);
                            formatted_cols.push({
                                value: col.value,
                                text: text,
                                category: category,
                                subcategory: subcategory,
                                type: col.type || null,
                                if_column_matched: isHiddenField ? true : apiValues.has(col.value),
                                if_sql_value: this.isSqlValue(col.value)
                            });
                        }

                        // Add any existing columns that weren't in the API response
                        existingColumns.forEach((existingCol, value) => {
                            if (!formatted_cols.some(col => col.value === value)) {
                                const isHiddenField = /(?:^|\.)_/.test(value);
                                formatted_cols.push({
                                    value: value,
                                    text: existingCol.text || value,
                                    category: existingCol.category || '',
                                    subcategory: existingCol.subcategory || null,
                                    type: existingCol.type || null,
                                    if_column_matched: isHiddenField ? true : false,
                                    if_sql_value: this.isSqlValue(value)
                                });
                            }
                        });

                        // Update if_column_matched and if_sql_value for existing columns
                        if (this.data_filter && this.data_filter.logic_units) {
                            this.data_filter.logic_units.forEach(unit => {
                                if (unit.db_column && unit.db_column.value) {
                                    const isHiddenField = /(?:^|\.)_/.test(unit.db_column.value);
                                    unit.db_column.if_column_matched = isHiddenField ? true : apiValues.has(unit.db_column.value);
                                    unit.db_column.if_sql_value = this.isSqlValue(unit.db_column.value);
                                }
                            });
                        }

                        _self.db_columns = formatted_cols;
                        _self.unfiltered_db_columns = JSON.parse(JSON.stringify(formatted_cols));
                    }
                    _self.is_loading = false;
                });
            },
            isSqlValue(value) {
                if (!value) return false;
                // Dot-separated column paths (e.g. giving_summary.first_gift.date) are not SQL
                if (/^[\w.]+$/.test(value) && value.indexOf('.') !== -1) return false;

                // Check for common SQL patterns
                const sqlPatterns = [
                    /\b(IF|CASE|WHEN|THEN|ELSE|END)\b/i,  // IF/CASE statements
                    /\b(SELECT|FROM|WHERE|GROUP BY|ORDER BY|HAVING|JOIN|LEFT|RIGHT|INNER|OUTER)\b/i,  // SQL keywords
                    /\b(COUNT|SUM|AVG|MAX|MIN)\s*\(/i,  // Aggregate functions
                    /\b(AND|OR|NOT|IN|EXISTS|BETWEEN|LIKE)\b/i,  // SQL operators
                    /\b(IS NULL|IS NOT NULL)\b/i,  // NULL checks
                    /[<>]=?|!=|=/,  // Comparison operators
                    /\(.*\)/,  // Parentheses (function calls or subqueries)
                    /\b(INTERVAL|DATE|DATETIME|TIMESTAMP)\b/i,  // Date/time functions
                    /\b(CURRENT_DATE|CURRENT_TIMESTAMP|NOW)\b/i,  // Current date/time functions
                    /\b(COALESCE|NULLIF|IFNULL)\b/i  // NULL handling functions
                ];

                return sqlPatterns.some(pattern => pattern.test(value));
            },
            shouldShowValues(unit) {
                const operator = unit?.operator != null && typeof unit.operator === 'object' ? unit.operator.value : unit?.operator;
                if (operator == null) return false;
                return operator !== 'IS NULL' && operator !== 'IS NOT NULL';
            },
            hasLogicUnitError(logic) {
                if (!logic || !logic.operator || !logic.db_column) {
                    return false;
                }

                const operator = typeof logic.operator === "object" ? logic.operator.value : logic.operator;
                const hasValue = logic.value !== null && logic.value !== "";

                if (!logic.db_column.value || !operator || (!hasValue && !operator.includes("NULL"))) {
                    return true;
                }

                // Check if database column is still available
                if (this.unfiltered_db_columns?.length > 0) {
                    const columnExists = this.unfiltered_db_columns.some(obj => obj.value === logic.db_column.value);
                    if (!columnExists) {
                        logic.db_column.value = "";
                        logic.db_column.text = "";
                        return true;
                    }
                }

                // Check static value selection
                if (logic.is_static && logic.value === null) {
                    return true;
                }

                // Check comparison column
                if (!logic.is_static && !logic.show_options && this.unfiltered_db_columns?.length > 0) {
                    const valueExists = this.unfiltered_db_columns.some(obj => obj.value === logic.value?.value);
                    if (!valueExists) {
                        if (logic.value) {
                            logic.value.text = "";
                            logic.value.value = "";
                        }
                        return true;
                    }
                }

                return false;
            },
            onColumnChange(unit) {
                if (!unit || !unit.db_column) return;
                // Prefer column-name guess for boolean so "Is First Donor Period" etc. aren't mis-typed as date from API
                const guessedFromName = unit.db_column.text ? this.guessDataType(unit.db_column.text) : null;
                if (guessedFromName === 'boolean' || guessedFromName === 'bool') {
                    unit.data_type = guessedFromName;
                } else if (unit.db_column.type) {
                    unit.data_type = unit.db_column.type;
                } else if (this.column_types && unit.db_column.value && this.column_types[unit.db_column.value]) {
                    unit.data_type = this.column_types[unit.db_column.value];
                } else if (guessedFromName) {
                    unit.data_type = guessedFromName;
                } else {
                    unit.data_type = 'string';
                }
                // Default value UI: date → datepicker; non-date → List of Options (fetch samples)
                if (this.isDate(unit.data_type)) {
                    unit.is_static = true;
                    unit.show_options = false;
                    if (unit.value != null && Array.isArray(unit.value)) unit.value = '';
                } else {
                    unit.is_static = true;
                    unit.show_options = true;
                    unit.value = '';
                    this.ensureOptionsLoaded(unit);
                }
            },
            changeDBColumn(unit) {
                if (!unit || !unit.db_column) return;

                // Set data_type based on available information
                if (unit.db_column.type) {
                    unit.data_type = unit.db_column.type;
                } else if (this.column_types && unit.db_column.value && this.column_types[unit.db_column.value]) {
                    unit.data_type = this.column_types[unit.db_column.value];
                } else {
                    unit.data_type = 'string'; // Default to string if no type information is available
                }

                if (unit.db_column.value && this.db_column_values[unit.db_column.value] === undefined) {
                    this.loadDatabaseValues(unit.db_column.value);
                }
            },
            ensureOptionsLoaded(unit) {
                if (unit?.db_column?.value && this.db_column_values[unit.db_column.value] === undefined) {
                    this.loadDatabaseValues(unit.db_column.value);
                }
            },
            loadDatabaseValues(db_column) {
                if(db_column == undefined || db_column == null || db_column == "")
                    return;

                var data = {
                    client: this.client,
                    source: this.source,
                    column: db_column,
                    limit: 1000,
                    data_source_id: this.data_source_id
                };

                var _self = this;

                this.db_column_values[db_column] = [
                    {
                        text: 'Loading Options...',
                        value: 'loading'
                    }
                ];
                window.axios.post('/api/bigquery/get_samples', data)
                    .then(response => {
                    //Remove the null value
                    for(var i = response.data.samples.length - 1; i >= 0; i--)
                        if(response.data.samples[i].name == null)
                            response.data.samples.splice(i, 1);

                    response.data.samples.sort((a, b) => (a.name > b.name ) ? 1 : -1);

                    if(response.data.samples.length > 0){
                        let value_list = [];
                        response.data.samples.forEach((value) =>{
                            if (value.name && value.name.trim() != ''){
                                value_list.push({
                                    category: '',
                                    text: value.name,
                                    value: value.name
                                })
                            }
                        })
                        _self.db_search[db_column] = (value_list.length >= 999);
                        _self.db_column_values[db_column] = value_list;
                        _self.unfiltered_db_column_values[db_column] = value_list;
                    } else{
                        _self.db_search[db_column] = false
                        _self.db_column_values[db_column] = [
                            {name: '** All values are blank **'}
                        ];
                        _self.unfiltered_db_column_values[db_column] = [
                            {name: '** All values are blank **'}
                        ];
                    }
                    _self.$forceUpdate();
                });
            },
            showCustomError() {
                var filter = this.data_filter;
                if(filter.filter_option != 'custom')
                    return false;
                var logic = filter.filter_logic.replace(/[^0-9\s]/g, '');
                var units = logic.split(" ");
                units = units.filter(function(entry) { return /\S/.test(entry); });

                //Convert the array of strings into numbers like they should be
                var max_unit = 0;
                for(var i = 0; i < units.length; i++) {
                    units[i] = parseInt(units[i]);
                    if(units[i] > max_unit)
                        max_unit = units[i];
                }

                //If I'm using a unit that doesn't exist
                if(max_unit > filter.logic_units.length) {
                    filter.logic_error_msg = "You are using a logical unit (#"+max_unit+") that does not exist below."
                    return true;
                }
                //If I'm not using a unit defined below
                for(var i = 0; i < filter.logic_units.length; i++) {
                    //If the logical statement doesn't include one of the logic units
                    if(!units.includes(i+1)) {
                        filter.logic_error_msg = "A logical unit (#"+(i+1)+") is defined below but not used in the logical statement."
                        return true;
                    }
                }

                if(!this.validAdvancedLogic(filter.filter_logic)){
                    filter.logic_error_msg = "The logic statement is not valid. Check the syntax and make any necessary corrections.";
                    return true;
                }

                return false;
            },
            updateFilterLogic(option) {
                if (this.is_updating) {
                    return;
                }

                const previousOption = this.data_filter.filter_option;

                if (previousOption === 'custom') {
                    this.cached_custom_logic = this.data_filter.filter_logic;
                }

                this.is_updating = true;
                this.data_filter.filter_option = option;

                if (option === 'custom') {
                    const fallbackLogic = this.buildSimpleLogic('all');
                    const trimmed = (this.cached_custom_logic || '').trim();
                    this.data_filter.filter_logic = trimmed !== '' ? this.cached_custom_logic : fallbackLogic;
                } else {
                    this.data_filter.filter_logic = this.buildSimpleLogic(option);
                }

                this.$nextTick(() => {
                    this.is_updating = false;
                });
            },
            addLogicUnit() {
                this.data_filter.logic_units.push(
                    {
                        db_column: { value: "" , text: "", category: ""},
                        data_type:'string',
                        operator: { value: "" , text: "", category: ""},
                        value:'',
                        is_static: true,
                        show_options: false,
                        modification:'',
                    });

                this.errors.logic_units.push(false);

                if (this.data_filter.filter_option === 'any' || this.data_filter.filter_option === 'all') {
                    this.data_filter.filter_logic = this.buildSimpleLogic(this.data_filter.filter_option);
                } else if (this.data_filter.filter_option === 'custom' && this.data_filter.filter_logic) {
                    const newUnitNumber = this.data_filter.logic_units.length;
                    const trimmed = this.data_filter.filter_logic.trim();
                    this.data_filter.filter_logic = trimmed ? `${trimmed} AND ${newUnitNumber}` : String(newUnitNumber);
                }
            },
            removeLogicUnit(index) {
                const removedNumber = index + 1;
                this.data_filter.logic_units.splice(index, 1);
                this.errors.logic_units.splice(index, 1);
                if (this.data_filter.filter_option === 'any' || this.data_filter.filter_option === 'all') {
                    this.data_filter.filter_logic = this.buildSimpleLogic(this.data_filter.filter_option);
                } else if (this.data_filter.filter_option === 'custom' && this.data_filter.filter_logic) {
                    this.data_filter.filter_logic = this.removeUnitFromCustomLogic(this.data_filter.filter_logic, removedNumber);
                }
            },
            removeUnitFromCustomLogic(logic, removedNumber) {
                if (!logic || !logic.trim()) return this.buildSimpleLogic('all');
                const n = String(removedNumber);
                let result = logic.trim();
                const bothSides = new RegExp('(\\s*(?:AND|OR)\\s*)\\b' + n + '\\b\\s*(?:AND|OR)\\s*', 'gi');
                const operatorThenNum = new RegExp('\\s*(?:AND|OR)\\s*\\b' + n + '\\b', 'gi');
                const numThenOperator = new RegExp('\\b' + n + '\\b\\s*(?:AND|OR)\\s*', 'gi');
                const standalone = new RegExp('\\b' + n + '\\b', 'g');
                result = result.replace(bothSides, '$1').replace(operatorThenNum, '').replace(numThenOperator, '').replace(standalone, '');
                result = result.replace(/\s+/g, ' ').trim();
                const maxUnit = this.data_filter.logic_units.length;
                for (let i = maxUnit + 1; i > removedNumber; i--) {
                    result = result.replace(new RegExp('\\b' + i + '\\b', 'g'), String(i - 1));
                }
                if (!result || !/\d/.test(result)) return this.buildSimpleLogic('all');
                return result;
            },
            buildSimpleLogic(option) {
                if (!this.data_filter || !Array.isArray(this.data_filter.logic_units) || this.data_filter.logic_units.length === 0) {
                    return '1';
                }

                const delimiter = option === 'any' ? ' OR ' : ' AND ';
                const parts = this.data_filter.logic_units.map((unit, idx) => `${idx + 1}`);
                const logic = parts.join(delimiter);

                return logic !== '' ? logic : '1';
            },
            updateOperator(unit) {
                if (!unit.operator) return;
                const operatorValue = typeof unit.operator === 'object' ? unit.operator.value : unit.operator;
                if (operatorValue === 'REGEXP_CONTAINS' || 
                    operatorValue.includes('IN') || 
                    operatorValue === 'LIKE' || 
                    operatorValue.includes('NULL')) {
                    unit.is_static = true;
                    unit.show_options = Array.isArray(unit.value) && unit.value.length > 0;
                }
                // --- ADDED: Clear file/dataset selection if switching away ---
                if (operatorValue.includes('IN_FILE') || operatorValue.includes('IN_DATASET')) {
                    if (!this.files_loaded) {
                        this.loadAvailableFiles();
                    }
                    unit.selected_file = null;
                    unit.selected_column = null;
                    unit.value = null;
                }
            },
            updateStaticValue(unit) {
                if (!unit.value) return;
                
                var val = String(unit.value).toLowerCase().trim();

                if(val === 'true' || val === 'false')
                    unit.data_type = 'bool';
                else if (!isNaN(val))
                    unit.data_type = 'float';
                else
                    unit.data_type = 'string';
            },
            updateDateValue(index, unit) {
                const numInput = this.$el.querySelector(`.date_num[data-index="${index}"]`);
                const comparisonInput = this.$el.querySelector(`.date_comparison[data-index="${index}"]`);
                
                if (!numInput || !comparisonInput) return;
                
                const num = numInput.value;
                const comparison = comparisonInput.value;
                
                if (num && comparison) {
                    unit.value = `DATE_SUB(CURRENT_DATE(), INTERVAL ${num} ${comparison})`;
                }
            },
            handleColumnDisplayOpen(el) {
                // Reset expanded states
                this.expanded_categories = [];
                this.expanded_subcategories = [];

                // First handle the selected column's category/subcategory
                const selectedColumn = this.data_filter.logic_units[el].db_column;
                if (selectedColumn && selectedColumn.category) {
                    // Don't expand the selected category initially
                    this.expanded_categories = [];
                    if (selectedColumn.subcategory) {
                        this.expanded_subcategories = [];
                    }
                }

                // Ensure all categories and subcategories are collapsed
                this.$nextTick(() => {
                    // Find all category headers and collapse them
                    const categoryHeaders = document.querySelectorAll('.category-header');
                    categoryHeaders.forEach(header => {
                        const category = header.dataset.category;
                        if (category) {
                            const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                            if (icon) {
                                icon.classList.add('fa-caret-right');
                                icon.classList.remove('fa-caret-down');
                            }
                            header.dataset.isexpanded = "false";
                        }
                    });

                    // Find all subcategory headers and collapse them
                    const subcategoryHeaders = document.querySelectorAll('.subcategory-header');
                    subcategoryHeaders.forEach(header => {
                        const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                        if (icon) {
                            icon.classList.add('fa-caret-right');
                            icon.classList.remove('fa-caret-down');
                        }
                        header.dataset.isexpanded = "false";
                    });
                });
            },
            expandCategory(cat, expand) {
                if (!cat) return false;
                
                const isExpanded = this.expanded_categories.includes(cat);
                const shouldExpand = expand === true || !isExpanded;

                // Update state arrays immediately for reactive updates
                if (shouldExpand && !this.expanded_categories.includes(cat)) {
                    this.expanded_categories.push(cat);
                } else if (!shouldExpand) {
                    this.expanded_categories = this.expanded_categories.filter(c => c !== cat);
                    // Remove all subcategories of this category
                    this.expanded_subcategories = this.expanded_subcategories.filter(sc => !sc.startsWith(cat + '-'));
                }

                // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
                this.$nextTick(() => {
                    // Find all elements related to this category
                    const categoryHeader = document.querySelector(`div[data-category='${cat}']`);
                    if (!categoryHeader) return;

                    // Update category header
                    const categoryIcon = categoryHeader.querySelector('.fa-caret-right, .fa-caret-down');
                    if (categoryIcon) {
                        categoryIcon.classList.toggle('fa-caret-right', !shouldExpand);
                        categoryIcon.classList.toggle('fa-caret-down', shouldExpand);
                    }
                    categoryHeader.dataset.isexpanded = shouldExpand;

                    // Find and update all related subcategory headers
                    const subcategoryHeaders = document.querySelectorAll(`div[data-subcategory='${cat}'].subcategory-header`);
                    subcategoryHeaders.forEach(header => {
                        header.style.display = shouldExpand ? "block" : "none";
                        if (!shouldExpand) {
                            header.dataset.isexpanded = "false";
                            const subcategoryIcon = header.querySelector('.fa-caret-right, .fa-caret-down');
                            if (subcategoryIcon) {
                                subcategoryIcon.classList.add('fa-caret-right');
                                subcategoryIcon.classList.remove('fa-caret-down');
                            }
                        }
                    });
                });

                return false;
            },
            expandSubcategory(cat, subcat, expand) {
                if (!cat || !subcat) return false;

                const subcategoryKey = `${cat}-${subcat}`;
                const isExpanded = this.expanded_subcategories.includes(subcategoryKey);
                const shouldExpand = expand === true || !isExpanded;

                // Update state array immediately for reactive updates
                if (shouldExpand && !this.expanded_subcategories.includes(subcategoryKey)) {
                    this.expanded_subcategories.push(subcategoryKey);
                } else if (!shouldExpand) {
                    this.expanded_subcategories = this.expanded_subcategories.filter(sc => sc !== subcategoryKey);
                }

                // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
                this.$nextTick(() => {
                    const header = document.querySelector(`div[data-subcategory='${cat}'][data-subcategory-name='${subcat}']`);
                    if (!header) return;

                    // Update header
                    const icon = header.querySelector('.fa-caret-right, .fa-caret-down');
                    if (icon) {
                        icon.classList.toggle('fa-caret-right', !shouldExpand);
                        icon.classList.toggle('fa-caret-down', shouldExpand);
                    }
                    header.dataset.isexpanded = shouldExpand;

                    // Find and update all options under this subcategory
                    const options = document.querySelectorAll(`div[data-subcategory='${cat}'][data-subcategory-name='${subcat}'] + div.suboption`);
                    options.forEach(option => {
                        option.style.display = shouldExpand ? "block" : "none";
                    });
                });

                return false;
            },
            isSubcategoryVisible(category, subcategory) {
                // On initial load, subcategory should only be visible if its parent category is expanded
                // If the category is not expanded, hide all its subcategories
                return this.expanded_categories.includes(category);
            },
            shouldShowOption(category, subcategory) {
                let classes = [];
                
                if (!category) {
                    classes.push('show');
                } else {
                    classes.push('category-option');
                    const categoryExpanded = this.expanded_categories.includes(category);
                    if (!categoryExpanded) return classes.join(' ');
                    
                    if (!subcategory) {
                        classes.push('show');
                    } else {
                        classes.push('subcategory-option');
                        const subcategoryKey = `${category}-${subcategory}`;
                        if (this.expanded_subcategories.includes(subcategoryKey)) {
                            classes.push('show');
                        }
                    }
                }
                
                return classes.join(' ');
            },
            handleHover(event) {
                // Remove selected and highlight classes from all options
                const allOptions = document.querySelectorAll('.vs__dropdown-option');
                allOptions.forEach(option => {
                    option.classList.remove('vs__dropdown-option--selected', 'vs__dropdown-option--highlight');
                });
            },
            handleOperatorHover(event) {
                // Remove selected and highlight classes from all options
                const allOptions = document.querySelectorAll('.vs__dropdown-option');
                allOptions.forEach(option => {
                    option.classList.remove('vs__dropdown-option--selected', 'vs__dropdown-option--highlight');
                });
            },
            expandOperatorCategory(category) {
                if (!category) return;
                
                const isExpanded = this.expanded_operator_categories.includes(category);
                const shouldExpand = !isExpanded;

                // Update state array immediately for reactive updates
                if (shouldExpand && !this.expanded_operator_categories.includes(category)) {
                    this.expanded_operator_categories.push(category);
                } else if (!shouldExpand) {
                    this.expanded_operator_categories = this.expanded_operator_categories.filter(c => c !== category);
                }

                // Use $nextTick to ensure DOM updates happen after Vue's reactive updates
                this.$nextTick(() => {
                    const categoryHeader = document.querySelector(`.operator-category[data-category='${category}']`);
                    if (!categoryHeader) return;

                    // Update category header
                    const categoryIcon = categoryHeader.querySelector('.fa-caret-right, .fa-caret-down');
                    if (categoryIcon) {
                        categoryIcon.classList.toggle('fa-caret-right', !shouldExpand);
                        categoryIcon.classList.toggle('fa-caret-down', shouldExpand);
                    }
                    categoryHeader.dataset.isexpanded = shouldExpand;

                    // Find and update all related options
                    const options = document.querySelectorAll(`.operator-option[data-category='${category}']`);
                    options.forEach(option => {
                        option.style.display = shouldExpand ? "block" : "none";
                    });
                });
            },
            handleOperatorOpen(index) {
                this.$nextTick(() => {
                    const options = document.querySelectorAll('.operator-select .suboption');
                    options.forEach(option => {
                        option.classList.add('show');
                    });
                });
            },
            getColumnTitle(col) {
                if (!col || !col.db_column) return '';
                return this.getColumnDisplayLabel(col);
            },
            getColumnDisplayLabel(unit) {
                if (!unit?.db_column) return '';
                const val = unit.db_column.value;
                if (!val) return unit.db_column.text || '';
                const source = this.db_columns || this.unfiltered_db_columns || [];
                const option = source.find((opt) => opt.value === val);
                if (option) {
                    const cat = option.category ? option.category + ': ' : '';
                    const sub = option.subcategory ? option.subcategory + ' › ' : '';
                    return cat + sub + (option.text || '');
                }
                if (val.indexOf('.') !== -1) {
                    const parts = val.split('.');
                    return parts.map((p) => this.propercase(p)).join(' : ');
                }
                return unit.db_column.text || this.propercase(val);
            },
            validAdvancedLogic(statement) {
                //Get each token which are numbers or parentheses
                statement = statement.replaceAll("(", " ( ").replaceAll(")", " ) ");
                const tokens = statement.match(/[\w()]+|[^\w\s()+-]+/g);
                let expectDigit = true;
                let parensCount = 0;

                //Loop through the parenthesis
                for (let i = 0; i < tokens.length; i++) {
                    const token = tokens[i];
                    //For open parenthesis, make sure this isn't the second one in a row and that we were expected a number
                    if (token === "(") {
                        if (!expectDigit)
                        return false;

                        expectDigit = true;
                        parensCount++;
                        //For closing paretheses, be looking for a number and not have more than two in a row
                    } else if (token === ")") {
                        if (expectDigit || parensCount === 0)
                        return false;

                        expectDigit = false;
                        parensCount--;
                    //Looking for a number...
                    } else if (/^\d+$/.test(token)) {
                        if (!expectDigit)
                        return false;

                        expectDigit = false;
                    } else if (token.toUpperCase() === "AND" || token.toUpperCase() === "OR") {
                        if (expectDigit)
                        return false;

                        expectDigit = true;
                    } else if (token.toUpperCase() === "NOT") {
                        expectDigit = true;
                    }
                    else
                        return false;
                }

                return !expectDigit && parensCount === 0;
            },
            setEditingIndex(index){
                this.editing_index = index;
                // Load options when opening List of Options if not yet loaded (e.g. boolean column)
                const unit = this.data_filter.logic_units[index];
                if (unit?.db_column?.value && this.db_column_values[unit.db_column.value] === undefined) {
                    this.loadDatabaseValues(unit.db_column.value);
                }
            },
            searchOptions(search, loading){
                let column_name = this.data_filter.logic_units[this.editing_index].db_column.value;

                if(!this.db_search[column_name]){
                    this.db_column_values[column_name] = this.unfiltered_db_column_values[column_name].filter((value) => value.text.toLowerCase().includes(search.toLowerCase()));
                } else{
                    this.db_column_values[column_name] = [
                        {
                            text: 'Loading Options...',
                            value: 'loading'
                        }
                    ];
                    var data = {
                        client: this.client,
                        source: this.source,
                        column: column_name,
                        limit: 1000,
                        data_source_id: this.data_source_id,
                        staging_table: (this.static_db_columns != null),
                        search: search
                    };

                    window.axios.post('/api/bigquery/get_samples', data)
                        .then(response => {
                            var db_values = [];

                            //Remove the null value
                            for(var i = response.data.samples.length - 1; i >= 0; i--){
                                if(response.data.samples[i].name == null)
                                    response.data.samples.splice(i, 1);
                                else {
                                    db_values.push(response.data.samples[i].name);
                                }
                            }
                            db_values.sort((a, b) => (a.name > b.name ) ? 1 : -1);

                            this.db_column_values[column_name] = db_values;
                            this.$forceUpdate();
                        });
                }
            },
            fetchOptions(search, loading) {
                //Reset the array
                this.db_columns = JSON.parse(JSON.stringify(this.unfiltered_db_columns));

                if(search == "") return;
                this.is_loading = true;

                // Tokenize search so "First Gift Date" matches giving_summary.first_gift.date (each word can be in category/subcategory/text)
                const searchTokens = search.toLowerCase().trim().split(/\s+/).filter(Boolean);

                //Look at each column
                for(var i = this.db_columns.length-1; i >= 0 ; i--) {
                    const item = this.db_columns[i];
                    
                    // Build one searchable string from category, subcategory, text, and raw value (e.g. "giving summary first gift date")
                    const searchableParts = [item.category, item.subcategory, item.text, item.value].filter(Boolean);
                    const searchableText = searchableParts.join(' ').toLowerCase().replace(/[._]/g, ' ');

                    // Keep item if every search token appears somewhere in the combined searchable string, or if it's a divider
                    const matchesSearch = searchTokens.length === 0 || searchTokens.every(token => searchableText.indexOf(token) !== -1);
                    if (!matchesSearch && item.text !== "") {
                        this.db_columns.splice(i, 1);
                    }
                }

                //Get the remaining categories and subcategories
                var cats = [];
                var subcats = new Map(); // Map to track subcategories and their matching options
                
                for(var i = 0; i < this.db_columns.length; i++) {
                    const item = this.db_columns[i];
                    if(item.category != null && item.text != "") {
                        if(!cats.includes(item.category)) {
                            cats.push(item.category);
                        }
                        
                        // Track subcategories that have matching options
                        if(item.subcategory) {
                            const key = `${item.category}-${item.subcategory}`;
                            if(!subcats.has(key)) {
                                subcats.set(key, false);
                            }
                            subcats.set(key, true);
                        }
                    }
                }

                //Remove subcategory headers that have no matching options
                for(var i = this.db_columns.length-1; i >= 0 ; i--) {
                    const item = this.db_columns[i];
                    if(item.text == "" && item.subcategory) {
                        const key = `${item.category}-${item.subcategory}`;
                        if(!subcats.get(key)) {
                            this.db_columns.splice(i, 1);
                        }
                    }
                }

                //Remove category headers that have no matching options
                for(var i = this.db_columns.length-1; i >= 0 ; i--) {
                    const item = this.db_columns[i];
                    if(item.text == "" && item.category != null && !cats.includes(item.category)) {
                        this.db_columns.splice(i, 1);
                    }
                }

                //Expand the categories and subcategories
                for(var i = 0; i < cats.length; i++) {
                    this.expandCategory(cats[i], true);
                    // Find and expand all subcategories for this category
                    subcats.forEach((hasMatches, key) => {
                        const [category, subcategory] = key.split('-');
                        if (category === cats[i] && hasMatches) {
                            this.expandSubcategory(category, subcategory, true);
                        }
                    });
                }

                this.is_loading = false;
                return true;
            },
            fetchOperators(search, loading) {
                //Reset the array
                this.operator_options = JSON.parse(JSON.stringify(this.unfiltered_operator_options));

                if(search == "") {
                    // Show all options when no search
                    this.$nextTick(() => {
                        const options = document.querySelectorAll('.operator-select .suboption');
                        options.forEach(option => {
                            option.classList.add('show');
                        });
                    });
                    return;
                }

                //Look at each operator
                for(var i = this.operator_options.length-1; i >= 0 ; i--) {
                    //If the search string isn't in the text or the category
                    if(this.operator_options[i].text.toLowerCase().indexOf(search.toLowerCase()) == -1
                        && (this.operator_options[i].category == null
                        || this.operator_options[i].category.toLowerCase().indexOf(search.toLowerCase()) == -1)
                        && this.operator_options[i].text != "" ) //And not a category divider
                        this.operator_options.splice(i, 1);
                }

                //Get the remaining categories
                var cats = [];
                for(var i = 0; i < this.operator_options.length; i++)
                    if(this.operator_options[i].category != null && !cats.includes(this.operator_options[i].category) && this.operator_options[i].text != "")
                        cats.push(this.operator_options[i].category);

                //Expand the categories that have matches
                this.expanded_operator_categories = cats;

                this.is_loading = false;
                return true;
            },
            initTooltips() {
                this.$nextTick(() => {
                    try {
                        const tooltipTriggerList = this.$el.querySelectorAll('[data-bs-toggle="tooltip"]');
                        [...tooltipTriggerList].forEach(tooltipTriggerEl => {
                            try {
                                // Check if Bootstrap is available
                                if (!window.bootstrap || !window.bootstrap.Tooltip) {
                                    console.warn('Bootstrap Tooltip is not available');
                                    return;
                                }

                                // Dispose any existing tooltip
                                const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
                                if (existingTooltip) {
                                    existingTooltip.dispose();
                                }

                                // Only create new tooltip if element is valid
                                if (tooltipTriggerEl && tooltipTriggerEl.getAttribute('data-bs-original-title')) {
                                    new window.bootstrap.Tooltip(tooltipTriggerEl, {
                                        trigger: 'hover',
                                        html: true,
                                        container: 'body'
                                    });
                                }
                            } catch (tooltipError) {
                                console.warn('Error initializing tooltip:', tooltipError);
                            }
                        });
                    } catch (error) {
                        console.warn('Error in initTooltips:', error);
                    }
                });
            },
            // --- ADDED: File/Dataset Support ---
            async loadAvailableFiles() {
                try {
                    this.is_loading = true;
                    this.file_search_offset = 0;
                    const [filesResponse, datasetsResponse] = await Promise.all([
                        window.axios.get(`/api/files/${this.client.url}/list/0`),
                        window.axios.get(`/api/data_set/${this.client.url}`),
                    ]);
                    const files = filesResponse.data.files
                        .filter(file => file.status === 'ready' && file.columns !== null)
                        .map(file => ({
                            value: file.id,
                            columns: Object.entries(file.columns).map(([key, value]) => ({
                                id: key,
                                label: value
                            })),
                            text: file.name,
                            created_at: file.created_at,
                            type: 'file'
                        }));
                    const datasets = (datasetsResponse.data.data || [])
                        .map(dataset => ({
                            value: dataset.id,
                            text: dataset.name,
                            columns: dataset.columns.map(column => ({
                                id: column.id,
                                label: column.display_name
                            })),
                            created_at: dataset.created_at,
                            type: 'dataset'
                        }));
                    this.available_files = files;
                    this.available_datasets = datasets;
                    this.total_files = files.length;
                    this.total_datasets = datasets.length;
                    this.hasMoreFiles = filesResponse.data.files.length == 25;
                    this.is_loading = false;
                    this.files_loaded = true;
                } catch (error) {
                    this.available_files = [];
                    this.available_datasets = [];
                    this.is_loading = false;
                    this.hasMoreFiles = false;
                    this.files_loaded = false;
                }
            },
            async searchFiles(search) {
                if (!search) {
                    this.loadAvailableFiles();
                    return;
                }
                try {
                    this.is_loading = true;
                    this.file_search_offset = 0;
                    this.file_search_pattern = search;
                    const [filesResponse, datasetsResponse] = await Promise.all([
                        window.axios.post(`/api/files/${this.client.url}/list_by_pattern`, {
                            pattern: '%' + search + '%',
                            offset: 0,
                            page_size: 25
                        }),
                        window.axios.get(`/api/data_set/${this.client.url}`),
                    ]);
                    const files = filesResponse.data.files
                        .filter(file => file.status === 'ready' && file.columns !== null)
                        .map(file => ({
                            value: file.id,
                            text: file.name,
                            columns: Object.entries(file.columns).map(([key, value]) => ({
                                id: key,
                                label: value
                            })),
                            created_at: file.created_at,
                            type: 'file'
                        }));
                    const datasets = (datasetsResponse.data.data || [])
                        .filter(dataset => dataset.name.toLowerCase().includes(search.toLowerCase()) && dataset.columns !== null)
                        .map(dataset => ({
                            value: dataset.id,
                            text: dataset.name,
                            columns: dataset.columns.map(column => ({
                                id: column.id,
                                label: column.display_name
                            })),
                            created_at: dataset.created_at,
                            type: 'dataset'
                        }));
                    this.available_files = files;
                    this.available_datasets = datasets;
                    this.total_files = files.length;
                    this.total_datasets = datasets.length;
                    this.hasMoreFiles = filesResponse.data.files.length == 25;
                    this.is_loading = false;
                } catch (error) {
                    this.available_files = [];
                    this.available_datasets = [];
                    this.is_loading = false;
                    this.hasMoreFiles = false;
                }
            },
            getAvailableOptions(unit) {
                if (!unit || !unit.operator) return [];
                const operator = typeof unit.operator === 'object' ? unit.operator.value : unit.operator;
                if (operator === 'IN_FILE' || operator === 'NOT_IN_FILE') {
                    return this.available_files;
                } else if (operator === 'IN_DATASET' || operator === 'NOT_IN_DATASET') {
                    return this.available_datasets;
                }
                return [];
            },
            getPlaceholderText(unit) {
                if (!unit || !unit.operator) return '';
                
                const operator = typeof unit.operator === 'object' ? unit.operator.value : unit.operator;
                
                if (operator === 'IN_FILE' || operator === 'NOT_IN_FILE') {
                    return 'Select a file...';
                } else if (operator === 'IN_DATASET' || operator === 'NOT_IN_DATASET') {
                    return 'Select a dataset...';
                }
                
                return '';
            },
            getLoadingText(unit) {
                if (!unit || !unit.operator) return '';
                
                const operator = typeof unit.operator === 'object' ? unit.operator.value : unit.operator;
                
                if (operator === 'IN_FILE' || operator === 'NOT_IN_FILE') {
                    return 'Loading files...';
                } else if (operator === 'IN_DATASET' || operator === 'NOT_IN_DATASET') {
                    return 'Loading datasets...';
                }
                
                return '';
            },
            isSegmentOperator(operator) {
                if (!operator) return false;
                
                const segmentOperators = ['IN_AUDIENCE', 'NOT_IN_AUDIENCE'];
                return segmentOperators.includes(operator.value);
            },
            shouldShowFileSelection(unit) {
                if (!unit || !unit.operator) return false;
                const operator = typeof unit.operator === 'object' ? unit.operator.value : unit.operator;
                return operator && (operator.indexOf('IN_FILE') !== -1 || operator.indexOf('IN_DATASET') !== -1);
            },
            formatDate(date) {
                return moment(date).format('MM/DD/YYYY');
            },
            async loadMoreFiles() {
                if (this.is_loading) return;

                try {
                    this.is_loading = true;
                    this.file_search_offset += 25;

                    // Store current scroll position
                    const dropdown = document.querySelector('.vs__dropdown-menu');
                    const scrollPosition = dropdown ? dropdown.scrollTop : 0;

                    let response;
                    if (this.file_search_pattern) {
                        response = await window.axios.post(`/api/files/${this.client.url}/list_by_pattern`, {
                            pattern: this.file_search_pattern,
                            offset: this.file_search_offset,
                            page_size: 25
                        });
                    } else {
                        response = await window.axios.get(`/api/files/${this.client.url}/list/${this.file_search_offset}`);
                    }

                    const newFiles = response.data.files
                        .filter(file => file.status === 'ready' && file.columns !== null)
                        .map(file => ({
                            value: file.id,
                            text: file.name,
                            columns: Object.entries(file.columns).map(([key, value]) => ({
                                id: key,
                                label: value
                            })),
                            created_at: file.created_at,
                            type: 'file'
                        }));

                    this.available_files = [...this.available_files, ...newFiles];
                    this.hasMoreFiles = response.data.files.length == 25;
                    this.is_loading = false;

                    // Restore scroll position after the DOM updates
                    this.$nextTick(() => {
                        if (dropdown) {
                            dropdown.scrollTop = scrollPosition;
                        }
                    });
                } catch (error) {
                    console.error('Error loading more files:', error);
                    this.is_loading = false;
                }
            },
            handleFileSelection(index) {
                const unit = this.data_filter.logic_units[index];
                if (!unit.selected_file) {
                    unit.selected_column = null;
                    unit.value = null;
                    return;
                }
                unit.selected_column = null;
                unit.value = null;
            },
            handleColumnSelection(index) {
                const unit = this.data_filter.logic_units[index];
                if (unit.selected_column) {
                    unit.value = {
                        file_id: unit.selected_file.value,
                        column: unit.selected_column.id
                    };
                }
            },
            // --- END File/Dataset Support ---
            shouldShowDate(unit) {
                if(unit == null || unit.operator == null)
                    return false;

                const opValue = typeof unit.operator === 'object' ? unit.operator.value : unit.operator;
                //If the operator is "IN_" then return false
                if(opValue && opValue.indexOf('IN_') != -1)
                    return false;

                let is_date = false;
                if(unit.data_type == null || unit.data_type == 'string') {
                    if(unit.db_column && unit.db_column.text)
                        is_date = this.guessDataType(unit.db_column.text) == 'date';
                } else {
                    is_date = unit.data_type.indexOf('date') > -1 || unit.data_type.indexOf('time') > -1;
                }

                //If it is a date and the unit.value  isn't in a date format, set it to a blank string
                if(is_date && unit.value != null && Array.isArray(unit.value))
                    unit.value = "";

                return is_date;
            },
            guessDataType(col){
                if(col){
                    col = col.toLowerCase();
                    //If it contains date, dte, utc, time, or timestamp, created, or updated
                    if((col.indexOf('date') > -1 && col.indexOf('consolidate') == -1 && col.indexOf('validate') == -1) || 
                        col.indexOf('dte') > -1 || col.indexOf('utc') > -1 ||
                        col.indexOf('time') > -1 || col.indexOf('timestamp') > -1 || col.indexOf('created') > -1 ||
                        col.indexOf('modified') > -1 || col.indexOf('updated') > -1)
                        return 'date';
                    //If it contains "is " or "has " or "flag"
                    else if(col.indexOf('is ') > -1 || col.indexOf('has ') > -1 || col.indexOf('flag') > -1
                        || col.indexOf('opt in') > -1 || col.indexOf('opt out') > -1|| col.indexOf('valid') > -1
                        || col.indexOf('can be') > -1 || col.indexOf('can have') > -1)
                        return 'boolean';
                    //If it contains "count" or "sum" or "avg" or "max" or "min" or amt or amount or rev or number
                    else if((col.indexOf('count') > -1 && col.indexOf('account') == -1) || col.indexOf('day') > -1 || col.indexOf('month') > -1
                        ||col.indexOf('year') > -1 ||  col.indexOf('sum') > -1 || col.indexOf('avg') > -1 ||
                        col.indexOf('max') > -1 || col.indexOf('min') > -1 || col.indexOf('amt') > -1 ||
                        col.indexOf('amount') > -1 || col.indexOf('rev') > -1 || col.indexOf('giving') > -1 ||
                        col.indexOf('total') > -1 || col.indexOf('number') > -1)
                        return 'number';
                    else
                        return 'string';
                }
                return 'string';
            },
            getDisplayText(text) {
                if (!text) return '';
                
                // Try different possible separators
                const separators = [' › ', ' > ', ' - ', ': '];
                for (const separator of separators) {
                    if (text.includes(separator)) {
                        const parts = text.split(separator);
                        return parts.pop();
                    }
                }
                
                return text;
            },
        }
    }
</script>
